import * as LabelPrimitive from "@radix-ui/react-label"
import { clsx } from "clsx"
import {
  createContext,
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
  useContext
} from "react"

import { Icon, type IconName } from "../Icon"
import { Text } from "../Text"
import styles from "./Field.module.css"

export interface FieldProps {
  children?: ReactNode
  footer?: ReactNode
  labelPosition?: "top" | "side"
  selected?: boolean
  onMouseOverChange?: (isMouseOver: boolean) => void
}

export const Field = forwardRef<HTMLDivElement, FieldProps>(function Field(
  props,
  ref
) {
  const {
    children,
    labelPosition = "top",
    onMouseOverChange,
    footer,
    selected,
    ...otherProps
  } = props

  return (
    <div className={clsx(styles.root, selected && styles.selected)}>
      <div
        {...otherProps}
        className={clsx(styles.children, styles[labelPosition])}
        ref={ref}
        onMouseEnter={onMouseOverChange && (() => onMouseOverChange(true))}
        onMouseLeave={onMouseOverChange && (() => onMouseOverChange(false))}
      >
        <FieldContext.Provider value={{ labelPosition }}>
          {children}
        </FieldContext.Provider>
      </div>
      {footer}
    </div>
  )
})

interface FieldIconProps {
  name: IconName
  disabled?: boolean
}

export function FieldIcon(props: FieldIconProps) {
  const { name, disabled } = props

  return (
    <div className={clsx(styles.icon, disabled && styles.disabled)}>
      <Icon name={name} size="small" color="grey11" />
    </div>
  )
}

interface FieldLabelProps {
  children: string
  htmlFor?: string
  id?: string
}

export function FieldLabel(props: FieldLabelProps) {
  const { children, ...otherProps } = props

  const { labelPosition } = useField()
  const textVariant = labelPosition === "top" ? "extraSmall" : "normal"

  return (
    <LabelPrimitive.Root {...otherProps} className={styles.label}>
      <Text color="grey11" component="span" variant={textVariant}>
        {children}
      </Text>
    </LabelPrimitive.Root>
  )
}

interface FieldControlProps {
  children?: ReactNode
  disabled?: boolean
}

export function FieldControl(props: FieldControlProps) {
  const { children, disabled } = props

  return (
    <div className={clsx(styles.control, disabled && styles.disabled)}>
      {children}
    </div>
  )
}

export function FieldFooter(props: PropsWithChildren) {
  const { children } = props

  return <div className={styles.footer}>{children}</div>
}

interface FieldContextValue {
  labelPosition: "top" | "side"
}

const FieldContext = createContext<FieldContextValue | undefined>(undefined)

function useField() {
  const field = useContext(FieldContext)
  if (!field) throw new Error("FieldContext is mandatory.")
  return field
}
